import { AnalyticsContext } from '@carvana/eventable-clickstream';
import { useContext, useRef } from 'react';
import {
  ErrorText,
  ExpiredCard,
  ExpiredPill,
  Header,
  InputErrorWrapper,
  PaymentInputWrapper,
  SoftPullCTA,
  SubText
} from '@/FiltersMenu/Budget/ExperimentBudget/ExperimentBudget.styles';
import RefreshIcon from '@/FiltersMenu/Budget/ExperimentBudget/RefreshIcon';
import FilterEvents from '~/constants/events';
import SearchSource from '~/constants/SearchSource';
import {
  useFinanceInfo,
  useHasExpiredSoftPull,
  useHasSoftPull
} from '~/contexts/FinanceAndTradeContext';
import { useSearchRequestId } from '~/contexts/InventoryContext';
import SearchEventsDataContext from '~/contexts/SearchEventsDataContext';
import {
  SoftPullLocations,
  useOpenSoftPullModal
} from '~/hooks/useToggleSoftPullModal';
import LockedIcon from '~/icons/LockedIcon';
import UnlockedIcon from '~/icons/UnlockedIcon';
import formatAndDropDecimal from '~/utilities/formatAndDropDecimal';
import { BudgetFilterKeys, RangeType } from '../constants';
import { useBudgetPanelErrors } from '../hooks';
import {
  useGetPaymentDropdownOptions,
  useUpdatePaymentFilters,
  useValidatePaymentInput
} from '../hooks/Payment';
import RangeInput from '../RangeInput';
import useRangeInput from '../RangeInput/useRangeInput';

const filterKey = BudgetFilterKeys.Finance;

export default function Payment() {
  const { maxMonthlyPayment, minDownPayment } = useFinanceInfo();
  const hasSoftPull = useHasSoftPull();
  const { setSearchSource } = useContext(SearchEventsDataContext);
  const termsExpired = useHasExpiredSoftPull();
  const { values, updateValues } = useRangeInput(filterKey);
  const updatePaymentFilters = useUpdatePaymentFilters(values);
  const validateInput = useValidatePaymentInput();
  const { errors, updateErrors } = useBudgetPanelErrors();
  const { trackEvent } = useContext(AnalyticsContext);
  const openSoftPullModal = useOpenSoftPullModal(
    SoftPullLocations.PriceAndPayment
  );
  const globalSearchRequestId = useRef(useSearchRequestId());

  const { monthlyOptions, cashDownOptions } = useGetPaymentDropdownOptions(
    maxMonthlyPayment,
    minDownPayment
  );

  return (
    <>
      <div
        className={`flex ${
          termsExpired ? 'justify-between' : 'flex-col gap-8'
        }`}
      >
        <Header>Shop by Payment</Header>
        {termsExpired ? (
          <ExpiredPill data-qa="expired-term-pill">Terms Expired</ExpiredPill>
        ) : (
          <SubText>
            {!hasSoftPull
              ? 'Get pre-qualified to see actual monthly payments for every vehicle.'
              : `Your minimum cash down is $${formatAndDropDecimal(
                  minDownPayment
                )} and your max monthly payment is $${formatAndDropDecimal(
                  maxMonthlyPayment
                )} based on your Carvana financing terms.`}
          </SubText>
        )}
      </div>

      <InputErrorWrapper>
        <PaymentInputWrapper>
          <p className="t-body-s text-blue-6">Max Monthly Payment</p>
          <RangeInput
            disabled={!hasSoftPull}
            errors={errors}
            filterKey={filterKey}
            isValidInput={validateInput}
            rangeOptions={monthlyOptions}
            rangeType={RangeType.MonthlyPayment}
            updateErrors={updateErrors}
            updateFilters={(rangeType, value) => {
              updatePaymentFilters(rangeType, value);
              setSearchSource(SearchSource.Facet);
              trackEvent(FilterEvents.monthlyPayment, {
                currentSearchRequestId: globalSearchRequestId.current
              });
            }}
            updateValues={updateValues}
            value={values?.[RangeType.MonthlyPayment]?.toString()}
          />
        </PaymentInputWrapper>
        <ErrorText
          className={errors?.[RangeType.MonthlyPayment] ? 'block' : 'hidden'}
        >
          {errors[RangeType.MonthlyPayment]}
        </ErrorText>
      </InputErrorWrapper>

      <InputErrorWrapper>
        <PaymentInputWrapper>
          <p className="t-body-s text-blue-6">Max Cash Down</p>
          <RangeInput
            disabled={!hasSoftPull}
            errors={errors}
            filterKey={filterKey}
            isValidInput={validateInput}
            rangeOptions={cashDownOptions}
            rangeType={RangeType.DownPayment}
            updateErrors={updateErrors}
            updateFilters={(rangeType, value) => {
              updatePaymentFilters(rangeType, value);
              setSearchSource(SearchSource.Facet);
              trackEvent(FilterEvents.cashDown, {
                currentSearchRequestId: globalSearchRequestId.current
              });
            }}
            updateValues={updateValues}
            value={values?.[RangeType.DownPayment]?.toString()}
          />
        </PaymentInputWrapper>
        <ErrorText
          className={errors?.[RangeType.DownPayment] ? 'block' : 'hidden'}
        >
          {errors[RangeType.DownPayment]}
        </ErrorText>
      </InputErrorWrapper>

      {termsExpired && (
        <ExpiredCard data-qa="expired-copy">
          Your finance terms expired. Refresh to see monthly payments on every
          vehicle.
        </ExpiredCard>
      )}

      {!hasSoftPull && (
        <SoftPullCTA
          className="group/payments"
          data-analytics-id={null}
          onClick={() => openSoftPullModal()}
          data-qa="soft-pull-cta"
        >
          {termsExpired ? (
            <>
              <RefreshIcon /> Refresh Your Terms
            </>
          ) : (
            <>
              <LockedIcon className="group-hover/payments:hidden" white />
              <UnlockedIcon
                className="group-hover/payments:block hidden"
                white={true}
              />
              Unlock Payments
            </>
          )}
        </SoftPullCTA>
      )}
    </>
  );
}
